/**
 * AI Chat Widget Component
 * Floating chat widget for AI-powered CRM assistant.
 * Features:
 * - Collapsible floating button
 * - Real-time chat with Claude Sonnet
 * - Quick suggestion prompts
 * - Session management
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  MessageSquare,
  X,
  Send,
  Sparkles,
  Loader2,
  ChevronDown,
  RotateCcw,
  Mic,
  MicOff,
  ArrowUpRight,
  Download,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Map an SPA location to a {record_type, record_id} so the backend
// can hydrate the entity the user is looking at when they ask "who
// is this client?" / "tell me about this deal" without spelling out
// the id.
function detectRecordContext(pathname, params) {
  // /contacts/:contactId or /leads-dashboard/.../:id
  const m = pathname.match(/\/(contacts|leads|clients)\/([\w-]+)/);
  if (m) return { record_type: 'contact', record_id: m[2] };
  const d = pathname.match(/\/deals\/([\w-]+)/);
  if (d) return { record_type: 'deal', record_id: d[1] };
  const b = pathname.match(/\/brokers\/([\w-]+)/);
  if (b) return { record_type: 'broker', record_id: b[1] };
  // Fall back to React Router params if available
  if (params?.contactId) return { record_type: 'contact', record_id: params.contactId };
  if (params?.dealId) return { record_type: 'deal', record_id: params.dealId };
  if (params?.brokerId) return { record_type: 'broker', record_id: params.brokerId };
  return null;
}

// Pretty title for the current page — shown to the assistant so it
// can refer to the page by name in its replies.
function pageTitle(pathname) {
  if (!pathname || pathname === '/') return 'Dashboard';
  const slug = pathname.split('/').filter(Boolean).pop() || 'page';
  return slug.split('-').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ');
}

const AIChatWidget = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Build the page-context payload sent with every chat message.
  // The backend uses it to (a) resolve "this client" / "this deal"
  // to a real record and (b) bias the assistant's answers to the
  // user's current screen.
  const buildPageContext = useCallback(() => {
    const filtersFromQs = Object.fromEntries(
      new URLSearchParams(location.search).entries(),
    );
    const rec = detectRecordContext(location.pathname, params);
    return {
      path: location.pathname,
      filters: Object.keys(filtersFromQs).length ? filtersFromQs : null,
      title: pageTitle(location.pathname),
      ...(rec || {}),
    };
  }, [location.pathname, location.search, params]);

  // Open the page the backend's navigation engine proposed. We use
  // SPA navigation (no full reload) and merge any extra filters
  // into the URL search params so the destination page's filter bar
  // picks them up.
  const openNavigation = useCallback((nav) => {
    if (!nav?.path) return;
    const search = new URLSearchParams();
    Object.entries(nav.filters || {}).forEach(([k, v]) => {
      if (v == null || v === '') return;
      search.set(k, String(v));
    });
    const qs = search.toString();
    navigate(qs ? `${nav.path}?${qs}` : nav.path);
    setIsOpen(false);  // close widget so the page is visible
  }, [navigate]);

  // POST the navigation payload back to the backend, which re-runs
  // the lookup at full export size and streams a PDF down. We do
  // the blob → object-URL → click dance so the file lands in the
  // user's Downloads folder with the suggested filename.
  const exportPdf = useCallback(async (nav) => {
    if (!nav?.entity) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/ai-chat/export-pdf?token=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entity: nav.entity,
            path: nav.path,
            filters: nav.filters,
            summary: nav.summary,
          }),
        },
      );
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }
      // Pull the filename out of the Content-Disposition header if
      // present, otherwise fall back to a sensible default.
      const cd = response.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m ? m[1] : `crm_${nav.entity || 'results'}.pdf`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  }, [token]);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch suggestions on mount
  useEffect(() => {
    if (token && isOpen) {
      fetchSuggestions();
    }
  }, [token, isOpen]);

  const fetchSuggestions = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-chat/suggestions?token=${token}`);
      const data = await response.json();
      if (data.success) {
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/ai-chat/message?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          session_id: sessionId,
          context_type: 'general',
          // Send what the user is currently looking at so the
          // assistant can answer "tell me about this client" etc.
          page_context: buildPageContext(),
        })
      });

      const data = await response.json();

      if (data.success) {
        if (!sessionId) {
          setSessionId(data.session_id);
        }

        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          // Backend includes a `navigation` payload when the engine
          // detected a navigation intent — we render an "Open" button
          // next to the bubble.
          navigation: data.navigation || null,
          // `pending_action` signals a confirm-then-execute flow
          // (action assistant). We use the existing yes/no text
          // path, no UI changes needed today.
          pendingAction: data.pending_action || null,
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Auto-open the destination IF the user explicitly asked
        // ("open …", "take me to …"). For "show me …" we wait for a
        // click on the Open button so context isn't yanked away.
        if (data.navigation && /^(open|take me|go to|navigate)\b/i.test(messageText)) {
          // small delay so the user sees the assistant's confirmation bubble first
          setTimeout(() => openNavigation(data.navigation), 600);
        }
      } else {
        const errorMessage = {
          role: 'assistant',
          content: `Sorry, I encountered an error: ${data.error || 'Unknown error'}`,
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I had trouble connecting. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const startNewChat = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-chat/sessions/new?token=${token}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setSessionId(data.session_id);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error creating new session:', error);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  if (!token) return null;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 ${
            isDarkMode 
              ? 'bg-purple-600 hover:bg-purple-500' 
              : 'bg-purple-500 hover:bg-purple-600'
          }`}
          data-testid="ai-chat-widget-button"
        >
          <Sparkles className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 z-50 w-96 h-[500px] rounded-2xl shadow-2xl flex flex-col overflow-hidden ${
            isDarkMode 
              ? 'bg-gray-900 border border-gray-700' 
              : 'bg-white border border-gray-200'
          }`}
          data-testid="ai-chat-widget-window"
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${
            isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-purple-50'
          }`}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-purple-600' : 'bg-purple-500'
              }`}>
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Treppan AI
                </h3>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  CRM Assistant
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={startNewChat}
                title="New chat"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <Sparkles className={`w-12 h-12 mx-auto mb-4 ${
                  isDarkMode ? 'text-purple-400' : 'text-purple-500'
                }`} />
                <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Hi {user?.full_name?.split(' ')[0] || 'there'}!
                </h4>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  I'm your AI CRM assistant. How can I help you today?
                </p>
                
                {/* Quick suggestions */}
                {suggestions.length > 0 && (
                  <div className="space-y-2">
                    {suggestions.slice(0, 3).map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          isDarkMode 
                            ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' 
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        msg.role === 'user'
                          ? isDarkMode
                            ? 'bg-purple-600 text-white'
                            : 'bg-purple-500 text-white'
                          : msg.isError
                            ? isDarkMode
                              ? 'bg-red-900/50 text-red-300'
                              : 'bg-red-100 text-red-700'
                            : isDarkMode
                              ? 'bg-gray-800 text-gray-100'
                              : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      {msg.navigation && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            onClick={() => openNavigation(msg.navigation)}
                            className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md transition-colors ${
                              isDarkMode
                                ? 'bg-purple-600/80 hover:bg-purple-500 text-white'
                                : 'bg-purple-500 hover:bg-purple-600 text-white'
                            }`}
                            data-testid="ai-chat-nav-open"
                          >
                            <ArrowUpRight className="w-3 h-3" />
                            Open page
                          </button>
                          <button
                            onClick={() => exportPdf(msg.navigation)}
                            className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md transition-colors border ${
                              isDarkMode
                                ? 'border-purple-600/60 text-purple-200 hover:bg-purple-900/40'
                                : 'border-purple-300 text-purple-700 hover:bg-purple-50'
                            }`}
                            data-testid="ai-chat-export-pdf"
                          >
                            <Download className="w-3 h-3" />
                            Export PDF
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className={`rounded-2xl px-4 py-3 ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
                    }`}>
                      <div className="flex items-center gap-2">
                        <Loader2 className={`w-4 h-4 animate-spin ${
                          isDarkMode ? 'text-purple-400' : 'text-purple-500'
                        }`} />
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Thinking...
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                disabled={isLoading}
                className={`flex-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : ''}`}
                data-testid="ai-chat-input"
              />
              <Button
                onClick={() => sendMessage(inputValue)}
                disabled={!inputValue.trim() || isLoading}
                size="icon"
                className={isDarkMode ? 'bg-purple-600 hover:bg-purple-500' : 'bg-purple-500 hover:bg-purple-600'}
                data-testid="ai-chat-send-button"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
